import { Inject, Injectable } from '@angular/core';
import Amplify, { Auth, Hub } from 'aws-amplify';
import { AuthenticationDetails, CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { BaseAuthService } from './base-auth.service';
import { COGNITO_CONFIGURATION, CognitoConfiguration } from '../config/cognito-configuration';
import { AuthState } from '../model/AuthState';
import { RegisterProps } from '../model/RegisterProps';
import { HttpHeaders } from '@angular/common/http';
import { IdpProvider } from '../model/IdpProvider';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { AuthApiService } from './auth-api.service';
import { from, Observable, of, Subject } from 'rxjs';
import {UserExistenceStatus} from "../model/UserExistenceStatus";

@Injectable()
export class CognitoAuthService implements BaseAuthService {
  authState: AuthState = {
    isAuthenticated: false
  }
  stateChangedObservable: Subject<AuthState> = new Subject<AuthState>();

  constructor(private authApiService: AuthApiService, @Inject(COGNITO_CONFIGURATION) private config: CognitoConfiguration) {
    Amplify.configure({
      Auth: {
        region: 'ap-southeast-1',
        userPoolId: config.userPoolId,
        userPoolWebClientId: config.userPoolWebClientId,
        authenticationFlowType: 'USER_PASSWORD_AUTH',
        oauth: {
          domain: `${config.domainPrefix}.auth.${config.region}.amazoncognito.com`,
          scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
          redirectSignIn: config.loginRedirect,
          redirectSignOut: config.logoutRedirect,
          responseType: 'code'
        }
      }
    });
  }

  private setCurrentState(authState: AuthState) {
    this.authState = authState;
    this.stateChangedObservable.next(authState);
  }

  private getAccessTokenFromSession(session: CognitoUserSession) {
    return session.getAccessToken().getJwtToken();
  }

  private getPayloadFromSession(session: CognitoUserSession) {
    return session.getIdToken().decodePayload();
  }

  private getCurrentSession(): Observable<CognitoUserSession | null> {
    return from(Auth.currentAuthenticatedUser().then((cognitoUser: CognitoUser) => {
      return Auth.userSession(cognitoUser).catch(() => null);
    }).catch(() => null));
  }

  private getSocialSessionVerification(token: string, payload: { [id: string]: any }): Observable<AuthState> {
    return this.authApiService.checkUserExistByToken({
      access_token: token
    }).pipe<any, AuthState>(mergeMap(({status}): Observable<AuthState> => {
      if (status !== UserExistenceStatus.NOT_EXISTS) {
        return of({
          isAuthenticated: true
        })
      } else {
        return of({
          isAuthenticated: false,
          shouldRedirectToConfirmSignUp: true,
          confirmSignUpParams: {
            email: payload.email,
            firstName: payload.given_name,
            lastName: payload.family_name,
            cognitoId: payload['cognito:username']
          }
        })
      }
    }), catchError(() => of({
      isAuthenticated: false,
      shouldLogout: true
    })));
  }

  private getCurrentSessionVerification(): Observable<AuthState> {
    return this.getCurrentSession().pipe(mergeMap((session: CognitoUserSession | null): Observable<AuthState> => {
      if (session) {
        const payload = this.getPayloadFromSession(session);
        // check if login from other identity provider
        if (payload.identities) {
          // check whether the particular email of the access token is exist in the reflaunt or not
          // only do for other identity provider as for user-pass login, it has been handled from migrate trigger
          const jwtToken = this.getAccessTokenFromSession(session);
          return this.getSocialSessionVerification(jwtToken, payload);
        } else {
          return of({
            isAuthenticated: true
          })
        }
      }
      return of({
        isAuthenticated: false
      });
    }))
  }

  private verifyCurrentSessionAndSetState() {
    this.getCurrentSessionVerification().subscribe((state: AuthState) => {
      this.setCurrentState(state);
    });
  }

  private async verifyCurrentSessionAndSetStateAsPromise() {
    const state = await this.getCurrentSessionVerification().toPromise();
    this.setCurrentState(state);
  }

  private runHubListener() {
    Hub.listen("auth", ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
          this.verifyCurrentSessionAndSetState();
          break;
        case 'signOut':
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          this.setCurrentState({
            isAuthenticated: false
          })
          break;
      }
    });
  }

  private async afterRegister(props: RegisterProps, session: CognitoUserSession) {
    const accessToken = this.getAccessTokenFromSession(session);
    await this.authApiService.confirmSignUp({
      access_token: accessToken,
      email: props.email
    }).toPromise();
    await this.authApiService.updateProfile({
      first_name: props.first_name,
      last_name: props.last_name,
      customer_type: props.customer_type,
      from: props.from
    }, new HttpHeaders({
      Authorization: 'Bearer ' + accessToken
    })).toPromise();
  }

  public async forgotPassword(email) {
    try {
      await Auth.forgotPassword(email, {
        redirectUrl: this.config.resetPasswordRedirect,
        isFromMigration: 'true'
      });
    } catch(e) {
      // always bypass the bypass message
      if (e.message && e.message.indexOf('bypass') > -1) {
        return;
      } else {
        throw e;
      }
    }
  }

  public async resetPassword(email: string, code: string, password: string) {
    await Auth.forgotPasswordSubmit(email, code, password);
  }

  public async logout() {
    await Auth.signOut({
      global: true
    });
  }

  /**
   * Register a user into cognito and automatically log the user in
   * @param props
   */
  public async register(props: RegisterProps) {
    if (props.confirmSignUpParams) {
      const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser();
      if (cognitoUser) {
        const session: CognitoUserSession = await Auth.userSession(cognitoUser);
        await this.afterRegister(props, session).then(() => {
          this.authState = {
            isAuthenticated: true
          };
          this.stateChangedObservable.next(this.authState);
        });
      }
    } else {
      await Auth.signUp({
        username: props.email,
        password: props.password,
        attributes: {
          email: props.email
        }
      });
      const authDetails = new AuthenticationDetails({
        Username: props.email,
        Password: props.password,
        ValidationData: {},
        ClientMetadata: undefined,
      });
      const user = new CognitoUser({
        Username: props.email,
        Pool: new CognitoUserPool({
          UserPoolId: this.config.userPoolId,
          ClientId: this.config.userPoolWebClientId
        })
      });
      user.setAuthenticationFlowType('USER_PASSWORD_AUTH');
      await new Promise((resolve, reject) => {
        user.authenticateUser(authDetails, {
          onSuccess:(session: CognitoUserSession) => {
            return this.afterRegister(props, session).then(() => {
              return Auth['authCallbacks'](user, resolve, reject).onSuccess(session);
            }, (e) => {
              reject(e);
            });
          },
          onFailure:(err) => {
            reject(err);
          }
        })
      })
    }
  }

  public async login(email: string, password: string): Promise<void> {
    await Auth.signIn(email, password, {
      isFromMigration: 'true'
    });
  }

  public async changePassword(old_password: string, new_password: string): Promise<void> {
    const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser();
    if (cognitoUser) {
      const session: CognitoUserSession = await Auth.userSession(cognitoUser);
      const accessToken = this.getAccessTokenFromSession(session);
      const data = {
        old_password,
        new_password,
        access_token: accessToken
      }
      await this.authApiService.changePassword(data).toPromise();
    }
  }

  public async changePasswordAsAdmin(new_password: string, user_id: number): Promise<void> {
    const data = {
      new_password,
      user_id
    }
    await this.authApiService.changePasswordAsAdmin(data).toPromise();
  }

  public async authenticateFromExternalIdp(idpProvider: IdpProvider): Promise<void> {
    if (idpProvider === IdpProvider.facebook) {
      await Auth.federatedSignIn({
        provider: CognitoHostedUIIdentityProvider.Facebook,
      });
    } else if (idpProvider === IdpProvider.google) {
      await Auth.federatedSignIn({
        provider: CognitoHostedUIIdentityProvider.Google,
      });
    }
  }

  listenToStateChanges(): Observable<AuthState> {
    this.verifyCurrentSessionAndSetStateAsPromise().catch((e) => {
      console.debug(e);
    }).finally(() => {
      this.runHubListener();
    });
    return this.stateChangedObservable;
  }

  isAuthenticated(): Observable<AuthState> {
    return this.getCurrentSessionVerification();
  }

  getAccessToken(): Observable<string> {
    return this.getCurrentSession().pipe(map((cognitoSession: CognitoUserSession|null) => {
      if (cognitoSession) {
        if (cognitoSession.isValid()) {
          return cognitoSession.getAccessToken().getJwtToken();
        }
      }
      return '';
    }))
  }

  async getUserProfileOfCurrentSession(cognitoSession) {
    let profile;
    const accessToken = this.getAccessTokenFromSession(cognitoSession);
    const header = new HttpHeaders({
      Authorization: 'Bearer ' + accessToken
    });
    try {
      profile = await this.authApiService.getProfile(header).toPromise();
      return profile;
    } catch(e) {
      if (e && e.error && e.error.message) {
        if (e.error.message === 'Cognito username is not associated') {
          const payload = cognitoSession.getIdToken().decodePayload()
          await this.authApiService.confirmSignUp({
            access_token: accessToken,
            email: payload.email
          }).toPromise();
          profile = await this.authApiService.getProfile(header).toPromise();
          return profile;
        }
      }
      throw e;
    }
  }

  async getUserProfile(): Promise<any> {
    const cognitoSession: CognitoUserSession|null = await this.getCurrentSession().toPromise();
    if (!cognitoSession) {
      throw new Error('no current session');
    }
    if (!cognitoSession.isValid()) {
      throw new Error('invalid session');
    }
    const profile = await this.getUserProfileOfCurrentSession(cognitoSession);
    if (!profile) {
      throw new Error('invalid profile');
    }
    return profile;
  }

  async  checkIfCognitoUserExist(): Promise<any> {
    const cognitoSession: CognitoUserSession|null = await this.getCurrentSession().toPromise();
    if (!cognitoSession) {
      throw new Error('no current session');
    }
    if (!cognitoSession.isValid()) {
      throw new Error('invalid session');
    }
    let result;
    const accessToken = this.getAccessTokenFromSession(cognitoSession);
    const payload = cognitoSession.getIdToken().decodePayload();
    const data =  {
      access_token: accessToken,
      email: payload.email
    };
    try {
      result = await this.authApiService.checkUserExistByToken(data).toPromise();
      if (result.status === 'NOT_EXISTS') {
        await this.authApiService.confirmSignUp({
          access_token: accessToken,
          email: payload.email
        }).toPromise();
      }
      return status;
    } catch(e) {
      throw e;
    }
  }

  async updateEmail(data: any): Promise<any> {
    const cognitoSession: CognitoUserSession|null = await this.getCurrentSession().toPromise();
    if (cognitoSession && cognitoSession.isValid()) {
      return this.authApiService.cognitoUpdateEmail({
        email: data.new_email,
        access_token: cognitoSession.getAccessToken().getJwtToken()
      }).toPromise();
    }
  }

  async verifyUpdateEmail(data: any): Promise<any> {
    return this.authApiService.cognitoVerifyUpdateEmail({
      email_confirm: data.new_email,
      username: data.username
    }).toPromise();
  }
}
